import { BaseTextFieldProps, CircularProgress, createStyles, makeStyles, TextField, Theme } from '@material-ui/core';
import { RenderInputParams, UseAutocompleteProps } from '@material-ui/lab';
// import match from 'autosuggest-highlight/match';
// import parse from 'autosuggest-highlight/parse';
import gql from 'graphql-tag';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InfiniteAutocompleteV2 } from '../../components/InfiniteAutocompleteV2';
import { retrieveQueryDataAsync } from '../../helpers/commonHelper';
import PersonOutlinedIcon from '@material-ui/icons/PersonOutlined';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        customRenderWrapper: {
            display: 'flex',
            margin: '0 -10px',
            width: '100%',
            '& > div': {
                padding: '0 10px',
                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                fontWeight: 400,
            },
            '& .icon-wrapper': {
                display: 'flex',
                alignItems: 'center',
            },
            '& .other': {
                flex: 1,
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center',
                '& .primary': {
                    fontSize: '14px',
                    lineHeight: 1.5,
                    letterSpacing: '0.00938em',
                    // wordBreak: 'break-all',
                    // wordWrap: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                },
                '& .secondary': {
                    color: 'rgba(0, 0, 0, 0.54)',
                    fontSize: '12px',
                    lineHeight: 1.43,
                    letterSpacing: '0.01071em',
                    // wordBreak: 'break-all',
                    // wordWrap: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                },
                '& .dataSource': {
                    fontStyle: 'italic',
                }
            }
        }
    })
);

interface PartyWebUserSelectorProps<
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined
  > extends Pick<BaseTextFieldProps, 'label' | 'variant' | 'fullWidth' | 'margin' | 'required' | 'error' | 'size' | 'helperText'>, Omit<UseAutocompleteProps<PartyWebUser, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'renderOption' | 'options' | 'getOptionLabel'>, Pick<PartyWebUserSelectorParams, 'matterId' | 'isExcludeUsedParty' | 'filter'> {
}

export function PartyWebUserSelector<Multiple extends boolean | undefined, DisableClearable extends boolean | undefined, FreeSolo extends boolean | undefined>(props: PartyWebUserSelectorProps<Multiple, DisableClearable, FreeSolo>) {

    const classes = useStyles();
    const [internalFilter, setInternalFilter] = useState('');
    const isExternallyControlledFilter = props.filter !== undefined;
    const effectiveFilter = isExternallyControlledFilter ? props.filter : internalFilter;
    
    const [accumulatedOptions, setAccumulatedOptions] = useState<PartyWebUser[]>([]);
    const [recordCount, setRecordCount] = useState(0);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isNextPageLoading, setIsNextPageLoading] = useState(false);

    // Derived — compare loaded count to total; no separate hasNextPage state needed.
    const hasNextPage = accumulatedOptions.length < recordCount;

    // Refs for closure-safe access inside stable callbacks
    const mountedRef = useRef(true);
    const isLoadingRef = useRef(false);         // guards against concurrent page fetches
    const queryKeyRef = useRef(0);              // incremented on variable change to discard stale results
    const accumulatedLengthRef = useRef(0);     // tracks offset without stale closure issues
    const recordCountRef = useRef(0);           // mirrors recordCount state for use in loadNextPage
    const matterIdRef = useRef(props.matterId);
    const isExcludeUsedPartyRef = useRef(props.isExcludeUsedParty);
    const filterRef = useRef(props.filter);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Keep query-variable refs current every render so loadNextPage always sends fresh values
    matterIdRef.current = props.matterId;
    isExcludeUsedPartyRef.current = props.isExcludeUsedParty;
    filterRef.current = effectiveFilter;

    // -----------------------------------------------------------------------
    // Initial fetch / reset — fires whenever the query variables change
    // -----------------------------------------------------------------------
    useEffect(() => {
        const capturedKey = queryKeyRef.current + 1;
        queryKeyRef.current = capturedKey;

        // Reset everything synchronously so the UI clears immediately
        isLoadingRef.current = false;
        accumulatedLengthRef.current = 0;
        recordCountRef.current = 0;
        setAccumulatedOptions([]);
        setRecordCount(0);
        setIsNextPageLoading(false);
        setIsInitialLoading(true);

        retrieveQueryDataAsync<PartyWebUserSelectorParams, PartyWebUSerQueryData>({
            query: PartyWebUSerQuery,
            params: {
                matterId: props.matterId,
                isExcludeUsedParty: props.isExcludeUsedParty,
                filter: effectiveFilter,
                offset: 0,
                first: 50,
            },
            refreshData: true,
        }).then((data) => {
            if (queryKeyRef.current !== capturedKey || !mountedRef.current) { return; }
            const items = data.partyWebUsers?.partyWebUser || [];
            const total = data.partyWebUsers?.recordCount ?? 0;
            accumulatedLengthRef.current = items.length;
            recordCountRef.current = total;
            setAccumulatedOptions(items);
            setRecordCount(total);
            setIsInitialLoading(false);
        }).catch(() => {
            if (queryKeyRef.current !== capturedKey || !mountedRef.current) { return; }
            setIsInitialLoading(false);
        });
    }, [props.matterId, props.isExcludeUsedParty, effectiveFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // -----------------------------------------------------------------------
    // Load next page — called by InfiniteLoader when the user scrolls near the bottom
    // -----------------------------------------------------------------------
    const loadNextPage = useCallback(() => {
        if (isLoadingRef.current) { return; }
        // Guard: don't fetch if we've already loaded everything
        if (accumulatedLengthRef.current >= recordCountRef.current && recordCountRef.current > 0) { return; }

        isLoadingRef.current = true;
        setIsNextPageLoading(true);

        const capturedKey = queryKeyRef.current;

        retrieveQueryDataAsync<PartyWebUserSelectorParams, PartyWebUSerQueryData>({
            query: PartyWebUSerQuery,
            params: {
                matterId: matterIdRef.current,
                isExcludeUsedParty: isExcludeUsedPartyRef.current,
                filter: filterRef.current,
                offset: accumulatedLengthRef.current,
                first: 50,
            },
            refreshData: true,
        }).then((data) => {
            if (queryKeyRef.current !== capturedKey || !mountedRef.current) { return; }
            const newItems = data.partyWebUsers?.partyWebUser || [];
            setAccumulatedOptions(prev => {
                const merged = [...prev, ...newItems];
                accumulatedLengthRef.current = merged.length;
                return merged;
            });
            isLoadingRef.current = false;
            setIsNextPageLoading(false);
        }).catch(() => {
            if (queryKeyRef.current !== capturedKey || !mountedRef.current) { return; }
            isLoadingRef.current = false;
            setIsNextPageLoading(false);
        });
    }, []); // stable — all mutable values accessed via refs

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        return {
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                    {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                    {params.InputProps.endAdornment}
                </React.Fragment>
            ),
        };
    };

    // matterId / isExcludeUsedParty / filter are query-only — exclude from autocomplete spread
        const { label, variant, fullWidth, margin, required, error, size, multiple, value, helperText,
            matterId, isExcludeUsedParty, filter,
            onInputChange,
            ...autocompleteProps } = props;

    return (
        <InfiniteAutocompleteV2
            {...autocompleteProps as any}
            name="partyWebUser"
            multiple={multiple}
            value={value}
            items={accumulatedOptions}
            hasNextPage={hasNextPage}
            isNextPageLoading={isNextPageLoading}
            loadNextPage={loadNextPage}
            itemSize={75}
            isLoading={isInitialLoading}
            disabledFilterOptions={true}
            inputValue={effectiveFilter || ''}
            // Keep server-side search in sync with user typing unless filter is externally controlled.
            onInputChange={(event, value, reason) => {
                if (!isExternallyControlledFilter && (reason === 'input' || reason === 'clear' || (reason === 'reset' && !multiple))) {
                    setInternalFilter(value);
                }
                if (onInputChange) {
                    onInputChange(event, value, reason);
                }
            }}
            // tslint:disable-next-line: jsx-no-lambda
            getOptionLabel={(option: PartyWebUser) => option.name}
            // tslint:disable-next-line: jsx-no-lambda
            getOptionSelected={(option: PartyWebUser, val: PartyWebUser) => option.id === val.id}
            // tslint:disable-next-line: jsx-no-lambda
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant={variant}
                    fullWidth={true}
                    margin="none"
                    required={required}
                    InputProps={getInputProps(params, isInitialLoading)}
                    error={error}
                    size={size}
                    helperText={helperText}
                />
            )}
            // tslint:disable-next-line: jsx-no-lambda
            renderOption={(option: PartyWebUser, { selected }) => {
                    return (
                        <div className={classes.customRenderWrapper}>
                            <div className="icon-wrapper">
                                <PersonOutlinedIcon fontSize="large"/>
                            </div>
                            <div className="other">
                                <div className="primary">{option.name}</div>
                                <div className="secondary">{option.email}</div>
                                <div className="secondary">{[option.phone, option.mobile].filter(Boolean).join(' | ')}</div>
                            </div>
                        </div>
                    );
                }}
        />
    );
}

export interface PartyWebUserSelectorParams {
    matterId: number;
    filter?: string;
    isExcludeUsedParty?: boolean;
    offset?: number;
    first?: number;
}

export interface PartyWebUSerQueryData {
    partyWebUsers: PartyWebUsers | null;
}

export interface PartyWebUsers {
    recordCount: number;
    partyWebUser: PartyWebUser[];
}

export interface PartyWebUser {
    id:         number;
    userId:     string | null;
    name:       string;
    email:      string;
    phone:      string;
    mobile:     null | string;
    nameGuid:   string | null;
}

const PartyWebUSerQuery = gql`
    query PartyWebUsers(
        $matterId: Int!
        $filter: String
        $isExcludeUsedParty: Boolean
        $offset: Int
        $first: Int
    ) {
        partyWebUsers(
            matterId: $matterId
            filter: $filter
            isExcludeUsedParty: $isExcludeUsedParty
            offset: $offset
            first: $first
        ) {
            recordCount
            partyWebUser {
                id
                userId
                name
                email
                phone
                mobile
                nameGuid
            }
        }
    }
`;
